
import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GlobalService } from '../../shared-services/global.service';
import { SortUtilService } from '../../utils/sort-util.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-global-filter',
  imports: [
    MatFormFieldModule,
    CommonModule,
    MatSelectModule,
    MatIconModule,
    NgxMatSelectSearchModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './global-filter.component.html',
  styleUrl: './global-filter.component.scss',
})
export class GlobalFilterComponent {
  selectedMarket: string[] = ['All'];
  selectedEngine: string[] = ['All'];
  selectedYear: string[] = ['All'];

  market: any[] = [];
  engine: any = [];
  year: string[] = [];
  filteredMarket: any[] = [];
  filteredEngines: any[] = [];
  filteredYears: string[] = [];
  marketSearchCtrl = new FormControl<string>('', { nonNullable: true });
  engineSearchCtrl = new FormControl<string>('', { nonNullable: true });
  yearSearchCtrl = new FormControl<string>('', { nonNullable: true });

  @Input() config!: any;

  private resetSub!: Subscription;
  private searchSub = new Subscription();

  constructor(
    private service: GlobalService,
    private sortUtil: SortUtilService
  ) {}

  currentYear: number = new Date().getFullYear();
  nextYear: number = this.currentYear + 1;
  Year_Previous: number = this.currentYear;
  year_filter:Boolean=true;
  
  

  ngOnInit() {
    this.resetSub = this.service.reset$.subscribe(() => {
      this.resetSelection();
    });

    this.searchSub.add(
      this.marketSearchCtrl.valueChanges.subscribe((text) => this.applyMarketFilter(text)),
    );
    this.searchSub.add(
      this.engineSearchCtrl.valueChanges.subscribe((text) => this.applyEngineFilter(text)),
    );
    this.searchSub.add(
      this.yearSearchCtrl.valueChanges.subscribe((text) => this.applyYearFilter(text)),
    );

    const savedFilters = this.service.getCurrentFilters();
    this.year_filter = this.config?.year_filter ?? true;

    this.service.getYear().subscribe({
      next: (data: any) => {
        this.year = (data?.responseData?.[0]?.years || []).map((y: any) => String(y));
        this.filteredYears = [...this.year];
        this.applyYearFilter(this.yearSearchCtrl.value);
      },
      error: () => {
        this.year = [];
        this.filteredYears = [];
      },
    });

    // Market & Engine from saved filters (or All)
    if (savedFilters) {
      this.selectedMarket = savedFilters.filter.cleaned_vertical?.length
        ? savedFilters.filter.cleaned_vertical
        : ['All'];

      this.selectedEngine = savedFilters.filter.final_em?.length
        ? savedFilters.filter.final_em
        : ['All'];

      // YEAR: use saved (if specific), else default from config flag
      this.selectedYear =
        (savedFilters.filter.new_year?.length &&
         !savedFilters.filter.new_year.includes('All'))
          ? savedFilters.filter.new_year
          : this.getDefaultYearFromConfig();
    } else {
      this.selectedMarket = ['All'];
      this.selectedEngine = ['All'];
      this.selectedYear = this.getDefaultYearFromConfig(); // <- default from config flag
    }

    // Compute current/next year based on the selectedYear
    this.initializeYear();
    this.setupYearChange();

    // Market + Engines
    this.service.getMarketSeg().subscribe((data: any) => {
      this.market = this.extractSegments(data);
      this.filteredMarket = [...this.market];
      this.applyMarketFilter(this.marketSearchCtrl.value);
      this.getEngine();
    });
  }

  /** Decide default year from config.filters.year_2026 flag. */
  private getDefaultYearFromConfig(): string[] {
    const flag = this.config?.year_2026;
    if (flag === true)  return ['2026'];
    if (flag === false) return ['2025'];
    return ['All']; // fallback if flag missing
  }

  initializeYear() {
    const yr = this.selectedYear?.[0];
    if (yr && yr !== 'All' && /^\d{4}$/.test(yr)) {
      this.currentYear = parseInt(yr, 10);
      this.nextYear = this.currentYear + 1;
    }
  }

  setupYearChange() {
    const now = new Date();
    const nextYearDate = new Date(now.getFullYear() + 1, 0, 1, 0, 0, 0, 0);
    const timeDiff = nextYearDate.getTime() - now.getTime();

    if (timeDiff > 0) {
      setTimeout(() => {
        this.changeYearToNext();
      }, timeDiff);
    }
  }

  // Change year to next year when time comes
  changeYearToNext() {
    this.currentYear = new Date().getFullYear();
    this.nextYear = this.currentYear + 1;
    this.selectedYear = [this.nextYear.toString()];
    this.emitFilters();
  }

  getEngine() {
    let market: string[] = [];
    if (this.selectedMarket.includes('All')) {
      market = [];
    } else {
      market = this.selectedMarket;
    }
    this.service.getEngineModel(market).subscribe((model: any) => {
      const engineModels = this.extractEngineModels(model);
      this.engine = this.sortUtil.sortByFirstNumber(engineModels);
      this.filteredEngines = [...this.engine];
      this.applyEngineFilter(this.engineSearchCtrl.value);
      if (this.selectedEngine.every((val) => !engineModels.includes(val))) {
        this.selectedEngine = ['All'];
      }
    });
  }

  ngOnDestroy() {
    this.resetSub.unsubscribe();
    this.searchSub.unsubscribe();
  }

  toggleAllSelection(type: string) {
    const selectedKey = `selected${type}` as keyof GlobalFilterComponent;
    const list = this[selectedKey] as string[];

    if (list.includes('All') || list.length === 0) {
      this[selectedKey] = ['All'] as any;
    } else {
      this[selectedKey] = [] as any;
    }

    // Special case for Market → refresh engines
    if (type === 'Market') {
      this.getEngine();
    }

    this.emitFilters();
  }

  togglePerOneSelection(type: string) {
    const selectedKey = `selected${type}` as keyof GlobalFilterComponent;
    const listKey = type.toLowerCase() as keyof GlobalFilterComponent;
    let selected = this[selectedKey] as string[];
    let fullList = this[listKey] as string[];

    if (selected.includes('All')) {
      selected = selected.filter((val: string) => val !== 'All');
    }

    if (selected.length === fullList.length || selected.length === 0) {
      this[selectedKey] = ['All'] as any;
    } else {
      this[selectedKey] = selected as any;
    }

    // Special case for Market → refresh engines
    if (type === 'Market') {
      this.getEngine();
    }

    this.emitFilters();
  }

  filterEngineSelection(text: string) {
    this.applyEngineFilter(text);
  }

  filterMarketSelection(text: string) {
    this.applyMarketFilter(text);
  }

  filterYearSelection(text: string) {
    this.applyYearFilter(text);
  }

  resetSelection() {
    this.selectedEngine = ['All'];
    this.selectedMarket = ['All'];
    this.selectedYear = ['All'];
    this.marketSearchCtrl.setValue('', { emitEvent: false });
    this.engineSearchCtrl.setValue('', { emitEvent: false });
    this.yearSearchCtrl.setValue('', { emitEvent: false });
    this.filteredMarket = [...this.market];
    this.filteredEngines = [...this.engine];
    this.filteredYears = [...this.year];
    this.getEngine();
    this.service.resetFilterSelection(false, true);
  }

  private applyMarketFilter(text: string): void {
    const query = (text || '').trim().toLowerCase();
    this.filteredMarket = !query
      ? [...this.market]
      : this.market.filter((item: any) =>
          String(item ?? '').trim().toLowerCase().includes(query),
        );
  }

  private applyEngineFilter(text: string): void {
    const query = (text || '').trim().toLowerCase();
    this.filteredEngines = !query
      ? [...this.engine]
      : this.engine.filter((item: any) =>
          String(item ?? '').trim().toLowerCase().includes(query),
        );
  }

  private applyYearFilter(text: string): void {
    const query = (text || '').trim().toLowerCase();
    this.filteredYears = !query
      ? [...this.year]
      : this.year.filter((item: string) =>
          String(item ?? '').trim().toLowerCase().includes(query),
        );
  }

  emitFilters() {
    this.service.setFilters({
      filter: {
        cleaned_vertical: this.selectedMarket,
        final_em: this.selectedEngine,
        new_year: this.selectedYear,
      },
    });
  }

  private extractSegments(data: any): string[] {
    return (
      data?.responseData?.[0]?.segments ||
      data?.responseList?.[0]?.responseData?.[0]?.segments ||
      []
    );
  }

  private extractEngineModels(data: any): string[] {
    return (
      data?.responseData?.[0]?.engineModels ||
      data?.responseList?.[0]?.responseData?.[0]?.engineModels ||
      []
    );
  }
}
